import { format } from "date-fns";

import { useMemo } from "react";

import { fmtDate, Mono, type ResourceConfig, ResourcePage } from "@/components/resource-page";
import { birthDateMax, birthDateMin, today } from "@/utils/dates";
import type { Translate } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

interface NonMember {
  id: number;
  visit_date: string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  birth_date: string;
  promo_code_id: number;
  promo_code: string | null;
  club_id: number | null;
  club_name: string | null;
  created_by_id: number | null;
  created_at: string;
  updated_by_id: number | null;
  /** Sent back as If-Match on a correction, so one over someone else's is refused. */
  updated_at: string;
}

const day = (value: string) => format(new Date(value), "MM/dd/yyyy");

export const nonMembersConfig = (t: Translate): ResourceConfig<NonMember> => ({
  title: t("nav.nonMembers"),
  singular: t("nm.singular"),
  endpoint: "/non-members",
  model: "non_member",
  // Newest first, off the primary key — the same default the other lists take.
  defaultSort: { key: "id", order: "desc" },
  // The Employees row: a small search and a funnel on the left, the screen's
  // own actions as icons on the right.
  toolbarVariant: "compact",
  // Add, view and edit as sheets over the list, the way Employees does it:
  // these records are short, and the table behind is the context for them.
  formVariant: "modal",
  viewVariant: "modal",
  searchPlaceholder: t("rp.searchShort"),
  // Removing a visit used to drop the row. Nothing references one, so nothing
  // was left stranded — which is exactly why it was so easy to lose: one
  // misdirected click took a day's entry with nothing to undo it. Delete now
  // files it in the archive, and the archive gives it back.
  archiveFilter: { name: "archived", value: "true", permission: "delete__non_member" },
  restore: { permission: "delete__non_member" },
  // Tick the rows, then one button above them: Delete on the list, Restore in
  // the archive. Each row is tried on its own server-side, so a page of forty
  // that hits three refusals still files the thirty-seven.
  bulk: { permission: "delete__non_member" },
  confirmDeleteMessage: t("geo.confirmArchive"),
  // The legacy Customize Column list, in its order: the seven a visit is read
  // by, then the record's own audit trail — which legacy carried too, and off
  // by default there as here. Id sits with the audit columns rather than
  // leading the table: nobody reads a walk-in by its primary key, and the row
  // menu still opens the record when the column is off.
  columns: [
    // The visit date is the day it was recorded — the form fixes it to today
    // and locks it — so "Created At" below is the same day printed twice,
    // which is exactly why legacy left it off.
    {
      key: "visit_date",
      label: t("nm.date"),
      sortKey: "visit_date",
      render: (r) => day(r.visit_date),
    },
    // Three columns rather than one line of name: the parts are stored apart,
    // read apart — a middle name is not a second first name — and searched for
    // in any combination, so the table shows them the way the search asks for
    // them. Only the given name sorts: the other two have no index behind
    // them, and a sort the server refuses is worse than a header that does
    // not offer one.
    {
      key: "first_name",
      label: t("nm.firstName"),
      sortKey: "first_name",
      render: (r) => <span className="text-fg font-medium">{r.first_name}</span>,
    },
    {
      key: "middle_name",
      label: t("nm.middleName"),
      render: (r) => r.middle_name ?? "",
    },
    {
      key: "last_name",
      label: t("nm.lastName"),
      render: (r) => r.last_name ?? "",
    },
    {
      key: "birth_date",
      label: t("nm.birthDate"),
      sortKey: "birth_date",
      render: (r) => <span className="text-fg-muted">{day(r.birth_date)}</span>,
    },
    {
      key: "promo_code",
      label: t("nm.promoCode"),
      render: (r) => (
        <Mono className="bg-surface-inset text-fg rounded-md px-1.5 py-0.5">
          {r.promo_code ?? "—"}
        </Mono>
      ),
      csv: (r) => r.promo_code,
    },
    { key: "club", label: t("nm.club"), render: (r) => r.club_name ?? "—" },
    {
      key: "id",
      label: t("rp.id"),
      sortKey: "id",
      render: (r) => <span className="text-accent underline underline-offset-4">{r.id}</span>,
    },
    // Who was at the door and wrote it down. Keyed on the column the server
    // names, the way Members keys its own, so the label and the field behind
    // it stay the same thing.
    {
      key: "created_by_id",
      label: t("nm.createdBy"),
      render: (r, lookup) => lookup("created_by_id", r.created_by_id),
      csv: (r) => r.created_by_id,
    },
    // Null until somebody corrects the visit — most rows are recorded once and
    // never touched, which is why these two are off unless asked for. Both
    // read their names from the actor filter's options, the only list of users
    // this screen loads.
    {
      key: "updated_by_id",
      label: t("nm.modifiedBy"),
      render: (r, lookup) => (r.updated_by_id ? lookup("created_by_id", r.updated_by_id) : "—"),
      csv: (r) => r.updated_by_id,
    },
    { key: "updated_at", label: t("nm.modifiedAt"), render: (r) => fmtDate(r.updated_at) },
    { key: "created_at", label: t("nm.createdAt"), render: (r) => fmtDate(r.created_at) },
  ],
  // Legacy's defaults: the visit as it was taken down, and the audit trail a
  // switch away.
  hiddenByDefault: ["id", "created_by_id", "updated_by_id", "updated_at", "created_at"],
  filters: [
    {
      name: "promo_code_id",
      label: t("nm.promoCodeFilter"),
      type: "fk",
      endpoint: "/promo-codes/compact",
    },
    { name: "club_id", label: t("nm.club"), type: "fk", endpoint: "/clubs/compact" },
    { name: "visited_from", label: t("nm.visitedFrom"), type: "date" },
    { name: "visited_to", label: t("nm.visitedTo"), type: "date" },
    { name: "created_by_id", label: t("nm.actor"), type: "fk", endpoint: "/users/compact" },
  ],
  fields: [
    // Shown, never chosen: the day a visit happened is the day it is recorded,
    // and a correction does not get to move it — that would not be fixing what
    // was entered about the visit, it would be filing it as a different one.
    {
      name: "visit_date",
      label: t("nm.date"),
      type: "date",
      required: true,
      disabled: true,
      max: today(),
    },
    { name: "first_name", label: t("nm.firstName"), required: true },
    { name: "middle_name", label: t("nm.middleName") },
    // A last name is expected of a walk-in, and the switch is how someone who
    // goes by one name is recorded as having none — rather than the field
    // being left blank, which reads the same as not having got to it yet. The
    // API takes null either way, and the one-person-one-promo rule already
    // counts a missing last name as ''.
    {
      name: "last_name",
      label: t("nm.lastName"),
      required: true,
      absentToggle: t("nm.noLastName"),
    },
    // The picker will not offer a day the API would refuse.
    {
      name: "birth_date",
      label: t("nm.birthDate"),
      type: "date",
      required: true,
      min: birthDateMin(),
      max: birthDateMax(),
    },
    // Only codes still running: a visit is someone coming in on an offer, so
    // an offer that has ended is not one they can have come in on. The filter
    // above still lists every code — the visits on a code outlive the code.
    {
      name: "promo_code_id",
      label: t("nm.promoCode"),
      type: "fk",
      required: true,
      endpoint: "/promo-codes/compact",
      params: { active_only: true },
    },
    // Shown, never chosen, like the day: a visit happens at the club of
    // whoever records it, and moving it to another club afterwards would be
    // filing a different visit rather than correcting this one.
    {
      name: "club_id",
      label: t("nm.club"),
      type: "fk",
      required: true,
      endpoint: "/clubs/compact",
      disabled: true,
    },
  ],
  detailLabels: {
    visit_date: t("nm.date"),
    first_name: t("nm.firstName"),
    middle_name: t("nm.middleName"),
    last_name: t("nm.lastName"),
    birth_date: t("nm.birthDate"),
    promo_code: t("nm.promoCode"),
    club_name: t("nm.club"),
  },
  // A visit is recorded on the day, at the club the person recording it works from.
  newDefaults: (profile) => ({
    visit_date: format(new Date(), "yyyy-MM-dd"),
    club_id: profile.club?.id.toString() ?? "",
  }),
  toForm: (r) => ({
    visit_date: r.visit_date,
    first_name: r.first_name,
    middle_name: r.middle_name ?? "",
    last_name: r.last_name ?? "",
    birth_date: r.birth_date,
    promo_code_id: String(r.promo_code_id),
    club_id: r.club_id?.toString() ?? "",
  }),
});

function NonMembersPage() {
  const t = useTranslation();
  const config = useMemo(() => nonMembersConfig(t), [t]);
  return <ResourcePage config={config} />;
}

export default NonMembersPage;
